// Shared data interfaces used across the library.
//
// Two sections. Public API types are re-exported from the package root: import
// them from there when building export schemas or handling library events.
// Internal types back the library's own infrastructure and may change.

import type { AppID } from './projectManagement.ts';

// ==============================================================================
// Public API
// ==============================================================================

// ── File import events ────────────────────────────────────────────────────────

export interface FileInfo {
  sizeBytes: number;
  fileType: string;
}

export type FileInfoArr = Array<FileInfo>;

// ── API errors ────────────────────────────────────────────────────────────────

/** Error shape returned by PHP and rethrown by the Paramate SDK (e.g. status 499 = aborted) */
export interface PhpApiError {
  status: number;
  requestId?: string;
  response?: { message?: string };
}

// ── Changelog & notifications ─────────────────────────────────────────────────

/** `msg` is a plain string or { prod, dev } to show different text per environment */
export interface ChangelogEntry {
  dateUnixMs?: number;
  msg: string | { prod: string; dev: string };
}

export type Changelog = Record<string, ChangelogEntry>;

/** Multi-source changelog keyed by source name (e.g. "app", "trcadScript") */
export type MultiChangelog = Record<string, Changelog>;

export interface StoredNotification {
  id: string;
  defaultText: string;
  dictKey: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: number;
}

// ── Export schema types ───────────────────────────────────────────────────────

export const MESH_TYPES = ['stl', '3mf', 'stp'] as const;
export type MeshType = typeof MESH_TYPES[number];

/** Dispatched on window as 'as-export-status'. operation is owned by the caller ('download', 'grabcad', etc.). */
export interface ExportStatusDetail {
  exporterId: string;
  operation:  string;
  /** 'cancelled' means the user stopped the export — treat it as back-to-idle, not a failure. */
  state:      'pending' | 'success' | 'error' | 'cancelled';
  message?:   string;
}

/** Dispatched on window as 'as-export-cancel' to stop an in-flight export. Omit exporterId to cancel any. */
export interface ExportCancelDetail {
  exporterId?: string;
}

/**
 * Dispatched on window as 'as-export-uncancellable' when an export passes the
 * point where stopping it is still possible — the data has left the app.
 */
export interface ExportUncancellableDetail {
  exporterId: string;
}

/** Dispatched on window as 'as-export-progress' once per file resolved inside ExportBuilder.resolve(). */
export interface ExportProgressDetail {
  exporterId: string;
  current: number;
  total:   number;
}

export type FileCategory =
  | 'workpiece'
  | 'outputPart' // deprecated — use 'trcadMesh'
  | 'trcadMesh'
  | 'brep'
  | 'visualization'
  | 'document'
  | 'custom'
  | 'configurator';

/**
 * Declarative schema for a single file entry in an ExportBuilder.
 *
 * - `data`: pre-set bytes OR an async function that provides them at export time.
 *   If omitted for mesh categories (`outputPart`, `workpiece`, `brep`), bytes are
 *   auto-fetched from as-configurator at resolve time.
 *   `trcadMesh` is the preferred category for configurator output parts.
 * - `finalize`: controls whether the configurator's finalized mesh is fetched (default `true`).
 *   Only meaningful for auto-fetched mesh categories.
 * - `beforeExport`: called before data is resolved. Use it to configure session state
 *   on as-configurator (e.g. set params for the upcoming mesh fetch).
 * - `children`: dep files nested under this entry (equivalent to passing the returned
 *   index as `parentIdx`).
 */
export type FileDataSchema = {
  fileKey?: string;
  fileName?: string;
  category: FileCategory;
  /** Format of the provided data, or target format for auto-fetch. */
  fileType?: string;
  /** Whether to fetch the finalized configurator mesh. Default `true`. Only applies to auto-fetched mesh categories. */
  finalize?: boolean;
  customData?: Record<string, any>;
  tags?: string[];
  /** Called before data is resolved. Use it to configure session state on as-configurator. */
  beforeExport?: () => Promise<void>;
  /** Pre-set bytes (ArrayBuffer or Blob), or an async function returning either.
   *  Blobs are converted to ArrayBuffer automatically.
   *  Overrides the default configurator auto-fetch for mesh categories. */
  data?: ArrayBuffer | Blob | (() => Promise<ArrayBuffer | Blob>);
  children?: FileDataSchema[];
};


// ==============================================================================
// Internal
// ==============================================================================

// ── Placeholders ──────────────────────────────────────────────────────────────

/** Placeholder for workpiece logic */
export interface Workpiece {}

// ── Viewport geometry ─────────────────────────────────────────────────────────

/** Flattened geometry arrays and rendering metadata for mesh serialization/restoration */
export interface VPData {
  vertices: Float32Array;
  normals: Float32Array;
  colors: Float32Array;
  drawGroups: Array<{ start: number; count: number; materialId: any }>;
  boundingBox: { min: number[]; max: number[]; center: number[] };
}

// ── File collection ───────────────────────────────────────────────────────────

export type FileIdx = number;

interface BaseFileData {
  /** ID of the app that generated the file */
  appSource: AppID;
  /** Unique index in the current session */
  fileIdx: FileIdx;
  /** Key used to link this file with external systems */
  fileKey: string;
  /** Display name of the file */
  fileName?: string;
  /** Raw file data */
  data: ArrayBuffer;
  /** Timestamp of creation */
  createdAt: Date;
  /** Optional index of a parent file (creates a tree structure) */
  parent?: FileIdx;
  /** Optional search/filter tags */
  tags?: string[];
  /** Whether to fetch the finalized configurator mesh. Default `true`. */
  finalize?: boolean;
  /** App-specific metadata storage */
  customData?: { [key: string]: any };
}

/** Categories that specifically require a 3D mesh (3mf/stl) for processing */
export const MESH_CATEGORIES = ['workpiece', 'outputPart' /* deprecated */, 'trcadMesh'] as const;
export type MeshCategory = (typeof MESH_CATEGORIES)[number];

/** Discriminated union: MeshCategory members are constrained to MeshType; all others accept any string fileType */
export type FileData =
  | (BaseFileData & { category: MeshCategory; fileType: MeshType })
  | (BaseFileData & { category: Exclude<FileCategory, MeshCategory>; fileType: string });

export interface FileExportArray extends Array<FileData> {}

/**
 * Branded type to ensure a string is never empty at the type level.
 * Requires an explicit cast (as ValidFilename) after runtime validation.
 */
export type ValidFilename = string & { readonly __brand: 'NonEmpty' };
